import { CheckIcon, ChevronsUpDownIcon, ShieldCheckIcon } from 'lucide-react'
import type { components } from '@joyful/openapi/types.gen'
import { AssignableUserRoleSchema, UserRoleSchema } from '@joyful/utils'
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  toast,
} from '../../../../core'
import { useApiQueryClient } from '../../../utils/reactQuery'
import { useCurrentUser } from '../../../utils/currentUserContext'
import { useUserDropdownMenu } from '../../../components/UserDropdownMenuContext'
import { setImpersonatedRole } from '../utils/roleImpersonation'

type UserRole = components['schemas']['UserRole']

export function SwitchRoleDropdownMenuItem() {
  const currentUser = useCurrentUser()
  const currentUserRoleMeta = UserRoleSchema.meta(currentUser.role)

  if (!currentUserRoleMeta.canImpersonateRoles) {
    return null
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>{`Impersonate role`}</DropdownMenuSubTrigger>

      <DropdownMenuPortal>
        <DropdownMenuSubContent className="max-w-96">
          <RoleImpersonationMenuItems />
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  )
}

export function MobileRoleImpersonationMenu() {
  const currentUser = useCurrentUser()
  const currentUserRoleMeta = UserRoleSchema.meta(currentUser.role)
  const effectiveRoleMeta = UserRoleSchema.meta(currentUser.effective_role)
  const isImpersonatingRole = currentUser.impersonated_role ? true : false

  if (!currentUserRoleMeta.canImpersonateRoles) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          colorVariant={isImpersonatingRole ? 'default' : 'muted'}
          endIcon={<ChevronsUpDownIcon className="size-3.5!" />}
        >
          <span className="truncate">
            {isImpersonatingRole
              ? `Impersonating ${effectiveRoleMeta.title}`
              : `Impersonate role`}
          </span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="max-w-80">
        <RoleImpersonationMenuItems />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function RoleImpersonationMenuItems() {
  const currentUser = useCurrentUser()
  const userDropdownMenu = useUserDropdownMenu()
  const isImpersonatingRole = currentUser.impersonated_role ? true : false
  const isJoyfulAdmin = currentUser.role === 'JOYFUL_ADMIN'

  const { queryClient } = useApiQueryClient()

  const handleSelectRole = (role: UserRole) => {
    if (role === currentUser.effective_role) {
      return
    }

    userDropdownMenu.actions.setUserDropdownMenuOpen(false)
    setImpersonatedRole(role === currentUser.role ? null : role)

    const targetRoleTitle = UserRoleSchema.meta(role).title
    toast.promise(queryClient.invalidateQueries(), {
      error: `Failed to refresh role`,
      loading: `Switching role to ${targetRoleTitle}…`,
      success: `Switched role to ${targetRoleTitle}.`,
    })
  }

  const selectableRoleDefinitions = AssignableUserRoleSchema.definitions.filter(
    (definition) => {
      return isJoyfulAdmin || definition.value !== 'JOYFUL_ADMIN'
    },
  )

  return selectableRoleDefinitions.map((definition) => {
    const isActive = currentUser.effective_role === definition.value
    const isImpersonatedRole =
      currentUser.impersonated_role === definition.value
    const isRealRole = currentUser.role === definition.value
    const Icon = definition.value === 'JOYFUL_ADMIN' ? ShieldCheckIcon : null
    return (
      <DropdownMenuItem
        key={definition.value}
        icon={Icon ? <Icon className="size-4" /> : <span className="size-4" />}
        endAdornment={isActive ? <CheckIcon className="size-4" /> : undefined}
        subtitle={definition.description}
        onSelect={() => {
          if (!isActive) {
            handleSelectRole(definition.value)
          }
        }}
      >
        <span className="inline-flex items-center gap-x-2">
          <span>{definition.title}</span>
          {isImpersonatedRole ? (
            <Badge size="xxs" variant="brand">
              {`Impersonating`}
            </Badge>
          ) : null}
          {isRealRole && isImpersonatingRole ? (
            <Badge size="xxs" variant="neutral">
              {`Real role`}
            </Badge>
          ) : null}
        </span>
      </DropdownMenuItem>
    )
  })
}
